import { type RefObject, useRef } from "react";

/**
 * Snapshot of scroll state captured before a render-window change.
 * Used to restore scroll position after React commits new DOM.
 */
export interface ScrollSnapshot {
  /** ID of the topmost visible message at capture time */
  anchorMessageId: string | null;
  /** Pixel offset from container top to anchor element top */
  anchorOffsetFromContainer: number;
  /** scrollHeight - scrollTop - clientHeight (distance from bottom) */
  scrollBottom: number;
}

/**
 * Signal-style scroll anchor: capture scroll state before a window shift,
 * restore it after React commits. All DOM queries are scoped to scrollRef
 * to avoid matching duplicate data-message-id nodes in the thread panel.
 *
 * Two restoration strategies:
 * - Primary: find anchor element by data-message-id, adjust scrollTop by
 *   the pixel offset delta.
 * - Fallback: if anchor element is gone (windowed out), use scrollBottom
 *   math which stays stable when items are removed from the top.
 *
 * Snapshot refresh timing:
 * - On every scroll event (rAF-throttled): capture() updates lastSnapshotRef
 * - Immediately before any dispatch(): capture() one final time — equivalent
 *   of Signal's getSnapshotBeforeUpdate
 * - ResizeObserver and useLayoutEffect read from lastSnapshotRef
 */
export function useScrollAnchor(scrollRef: RefObject<HTMLDivElement | null>) {
  const lastSnapshotRef = useRef<ScrollSnapshot | null>(null);

  /**
   * Capture the current scroll state. Call on every scroll event (rAF-
   * throttled) and immediately before any window dispatch.
   */
  const capture = (): ScrollSnapshot | null => {
    const el = scrollRef.current;
    if (!el) return null;

    const containerTop = el.getBoundingClientRect().top;

    // Find the topmost visible message: first [data-message-id] whose
    // bottom edge is below the container's top edge.
    // Scoped to scrollRef — never document — to avoid thread panel dupes.
    const messageNodes = el.querySelectorAll<HTMLElement>("[data-message-id]");

    let anchorMessageId: string | null = null;
    let anchorOffsetFromContainer = 0;

    for (const node of messageNodes) {
      if (node.getBoundingClientRect().bottom > containerTop + 1) {
        anchorMessageId = node.dataset.messageId ?? null;
        anchorOffsetFromContainer =
          node.getBoundingClientRect().top - containerTop;
        break;
      }
    }

    const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    const snapshot: ScrollSnapshot = {
      anchorMessageId,
      anchorOffsetFromContainer,
      scrollBottom,
    };

    lastSnapshotRef.current = snapshot;
    return snapshot;
  };

  /**
   * Restore scroll position from a snapshot.
   *
   * Primary: find the anchor element and adjust scrollTop so its offset
   * from container top matches the saved value.
   *
   * Fallback: if anchor is gone, use scrollBottom math:
   * scrollTop = scrollHeight - clientHeight - savedScrollBottom
   * This stays stable when items are removed from the top.
   */
  const restore = (snapshot?: ScrollSnapshot | null) => {
    const s = snapshot ?? lastSnapshotRef.current;
    if (!s) return;

    const el = scrollRef.current;
    if (!el) return;

    if (s.anchorMessageId) {
      const anchorEl = el.querySelector<HTMLElement>(
        `[data-message-id="${CSS.escape(s.anchorMessageId)}"]`,
      );
      if (anchorEl) {
        const containerTop = el.getBoundingClientRect().top;
        const currentOffset =
          anchorEl.getBoundingClientRect().top - containerTop;
        const delta = currentOffset - s.anchorOffsetFromContainer;
        el.scrollTop += delta;
        return;
      }
    }

    // Fallback: scrollBottom math — stable when top items removed
    el.scrollTop = el.scrollHeight - el.clientHeight - s.scrollBottom;
  };

  const clear = () => {
    lastSnapshotRef.current = null;
  };

  return { capture, restore, clear, lastSnapshotRef };
}
